import React, { useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Checkbox, FormControlLabel, IconButton, Popover, Stack, Typography } from "@mui/material";
import { PDFTypeIcon } from "assets/material-custom-icons";
import { BreadcrumbActions } from "components/Bread-crumbs/BreadcrumbActionsSlot";
import { useInvestorsSelector } from "store";

interface Props {
  onPreview: () => void;
  onExportPDF: () => void;
  onBatchExport: (investors: string[]) => void;
}

export const HeaderActions: React.FC<Props> = ({ onPreview, onExportPDF, onBatchExport }) => {
  const investors = useInvestorsSelector();
  const allInvestors = investors.map((inv) => inv.name);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedInvestors, setSelectedInvestors] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  const handleToggleInvestor = (investor: string) => {
    setSelectedInvestors((prev) =>
      prev.includes(investor) ? prev.filter((i) => i !== investor) : [...prev, investor]
    );
  };

  const handleBatchExport = () => {
    if (!selectedInvestors.length) return;
    setAnchorEl(null);
    onBatchExport(selectedInvestors);
    setSelectedInvestors([]);
  };

  return (
    <BreadcrumbActions>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Button
          onClick={onPreview}
          variant="outlined"
          sx={{
            height: "24px",
            minHeight: 0,
            p: "0 14px",
            borderRadius: "999px",
            bgcolor: "#FFFFFF",
            color: "#071f35",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "none",
            flexShrink: 0,
            "&:hover": { bgcolor: "#f6f8fc" }
          }}
        >
          Preview
        </Button>

        <Box sx={{ position: "relative", flexShrink: 0, display: "flex", gap: 0 }}>
          <Button
            startIcon={<PDFTypeIcon sx={{ fill: "#FFFFFF", width: 16, height: 16 }} />}
            onClick={onExportPDF}
            sx={{
              height: "24px",
              minHeight: 0,
              p: "0 14px",
              border: "none",
              borderRadius: "999px 0 0 999px",
              bgcolor: "#071f35",
              color: "#FFFFFF",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "0 8px 16px rgba(7,31,53,0.2)",
              "&:hover": { bgcolor: "#0a2e4f" }
            }}
          >
            Export PDF
          </Button>
          <Button
            onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
            title="Batch export by investor"
            sx={{
              height: "24px",
              minHeight: 0,
              p: "0 8px",
              border: "none",
              minWidth: 0,
              borderLeft: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "0 999px 999px 0",
              bgcolor: "#071f35",
              color: "#FFFFFF",
              fontSize: 10,
              boxShadow: "0 8px 16px rgba(7,31,53,0.2)",
              "&:hover": { bgcolor: "#0a2e4f" }
            }}
          >
            {open ? <ArrowDropUpIcon sx={{ fontSize: 16 }} /> : <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          </Button>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  borderRadius: "12px",
                  border: "1px solid #dbe8f5",
                  boxShadow: "0 12px 32px rgba(7,31,53,0.14)",
                  width: 280,
                  overflow: "hidden"
                }
              }
            }}
          >
            <Box sx={{ p: "14px 16px 10px", borderBottom: "1px solid #dbe8f5" }}>
              <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#071f35" }}>Batch Export</Typography>
                <IconButton onClick={() => setAnchorEl(null)}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              <Typography sx={{ fontSize: 11, color: "#496682", mt: "2px" }}>Generate one PDF per investor</Typography>
            </Box>

            <Box sx={{ maxHeight: 220, overflowY: "auto", p: "6px 0" }}>
              {allInvestors.map((investor) => {
                const checked = selectedInvestors.includes(investor);
                return (
                  <FormControlLabel
                    key={investor}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() => handleToggleInvestor(investor)}
                        sx={{ p: "4px", "& .MuiSvgIcon-root": { fontSize: 18 } }}
                      />
                    }
                    label={investor}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      m: 0,
                      p: "5px 16px",
                      bgcolor: checked ? "#F6F8FC" : "transparent",
                      "& .MuiFormControlLabel-label": {
                        fontSize: 13,
                        color: "#071f35",
                        fontWeight: checked ? 600 : 400,
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }
                    }}
                  />
                );
              })}
            </Box>

            <Box sx={{ p: "10px 16px", borderTop: "1px solid #dbe8f5", display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                onClick={() =>
                  setSelectedInvestors(selectedInvestors.length === allInvestors.length ? [] : [...allInvestors])
                }
                sx={{
                  color: "#26A6AE",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "none",
                  p: "8px 14px",
                  minWidth: 0
                }}
              >
                {selectedInvestors.length === allInvestors.length ? "Deselect all" : "Select all"}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                onClick={handleBatchExport}
                disabled={selectedInvestors.length === 0}
                sx={{
                  p: "7px 14px",
                  borderRadius: "999px",
                  minWidth: 0,
                  bgcolor: selectedInvestors.length > 0 ? "#071f35" : "#dbe8f5",
                  color: selectedInvestors.length > 0 ? "#FFFFFF" : "#92a7bc",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: selectedInvestors.length > 0 ? "#0a2e4f" : "#dbe8f5" },
                  "&.Mui-disabled": { bgcolor: "#dbe8f5", color: "#92a7bc" }
                }}
              >
                Export{" "}
                {selectedInvestors.length > 0
                  ? `${selectedInvestors.length} PDF${selectedInvestors.length > 1 ? "s" : ""}`
                  : "..."}
              </Button>
            </Box>
          </Popover>
        </Box>
      </Box>
    </BreadcrumbActions>
  );
};
